import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  api,
  type DocumentReadingSummary,
  type ReadingEntry,
  type User,
} from '../api/client';
import { AvisoError } from '../components/Aviso';
import { GraficaTemporal, type PuntoTemporal } from '../components/GraficaTemporal';
import { BarrasComparativas } from '../components/BarrasComparativas';
import { duracionLegible } from '../formato';
import { claveDeConsulta, claveVisible } from '../documentos/clave';

/**
 * Comportamiento de lectura de una persona.
 *
 * Responde lo que una revisión pregunta sobre alguien en concreto: qué ha
 * consultado, cuándo y cuánto tiempo. Está separada de la ficha administrativa
 * —roles y grupos— a propósito: son dos preguntas distintas y quien audita no
 * necesita poder cambiar permisos para responder la suya.
 */
export function EmpleadoDetalle() {
  const { t, i18n } = useTranslation();
  const { userId } = useParams<{ userId: string }>();

  const [persona, setPersona] = useState<User | null>(null);
  const [serie, setSerie] = useState<PuntoTemporal[]>([]);
  const [porDocumento, setPorDocumento] = useState<DocumentReadingSummary[]>([]);
  const [ultimas, setUltimas] = useState<ReadingEntry[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [cargando, setCargando] = useState(true);

  const fechaHora = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'short',
  });

  useEffect(() => {
    if (!userId) return;
    setCargando(true);

    Promise.all([
      api.users(`?pageSize=100`),
      api.accessTimeline(`userId=${userId}`),
      api.accessLogByDocument(`userId=${userId}`),
      api.accessLog(`userId=${userId}&pageSize=15`),
    ])
      .then(([usuarios, dias, documentos, consultas]) => {
        setPersona(usuarios.items.find((u) => u.id === userId) ?? null);
        setSerie(dias.map((dia) => ({ dia: dia.day, valores: [dia.sessions] })));
        setPorDocumento(documentos);
        setUltimas(consultas.items);
        setError(null);
      })
      .catch(setError)
      .finally(() => setCargando(false));
  }, [userId]);

  const totalSegundos = porDocumento.reduce((suma, doc) => suma + doc.totalSeconds, 0);
  const totalConsultas = porDocumento.reduce((suma, doc) => suma + doc.sessions, 0);

  if (cargando) return <p className="tenue">{t('app.loading')}</p>;

  return (
    <>
      <div className="pagina__encabezado pagina__encabezado--con-vuelta">
        <Link className="migaja" to="/usuarios">
          ← {t('people.backToList')}
        </Link>
        <h1 className="pagina__titulo">
          <span className="mono pagina__clave">{persona?.username ?? userId}</span>
          {persona?.fullName ?? t('people.unknown')}
        </h1>
      </div>

      <AvisoError error={error} />

      <section className="fichas" aria-label={t('people.indicators')}>
        <div className="ficha">
          <span className="ficha__etiqueta">{t('access.count', { count: 0 }).replace(/^\d+\s*/, '')}</span>
          <span className="ficha__valor">{totalConsultas}</span>
        </div>
        <div className="ficha">
          <span className="ficha__etiqueta">{t('charts.documentsRead')}</span>
          <span className="ficha__valor">{porDocumento.length}</span>
        </div>
        <div className="ficha">
          <span className="ficha__etiqueta">{t('dashboard.readingTime')}</span>
          <span className="ficha__valor">{duracionLegible(totalSegundos)}</span>
        </div>
      </section>

      <div className="rejilla">
        <section className="tarjeta">
          <div className="tarjeta__encabezado">
            <h2 className="tarjeta__titulo">{t('charts.readingOverTime')}</h2>
          </div>
          <GraficaTemporal
            puntos={serie}
            series={[{ nombre: t('charts.readings'), color: 'var(--serie-1)' }]}
          />
          <p className="tarjeta__pie">{t('charts.readingOverTimeNote')}</p>
        </section>

        <section className="tarjeta">
          <div className="tarjeta__encabezado">
            <h2 className="tarjeta__titulo">{t('charts.documentsRead')}</h2>
            <span className="tarjeta__nota">{porDocumento.length}</span>
          </div>
          {porDocumento.length === 0 ? (
            <p className="vacio">{t('access.empty')}</p>
          ) : (
            <BarrasComparativas
              unidad={t('dashboard.consultations')}
              datos={porDocumento.slice(0, 8).map((documento) => ({
                clave: documento.documentId,
                etiqueta: claveVisible(documento),
                valor: documento.sessions,
              }))}
            />
          )}
          <p className="tarjeta__pie">{t('people.mostReadNote')}</p>
        </section>
      </div>

      <section className="tarjeta">
        <div className="tarjeta__encabezado">
          <h2 className="tarjeta__titulo">{t('people.lastReadings')}</h2>
          <Link className="boton boton--discreto" to={`/consultas?userId=${userId ?? ''}`}>
            {t('dashboard.seeAccessLog')}
          </Link>
        </div>
        {ultimas.length === 0 ? (
          <p className="vacio">{t('access.empty')}</p>
        ) : (
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  <th>{t('access.document')}</th>
                  <th>{t('access.startedAt')}</th>
                  <th>{t('access.duration')}</th>
                  <th>{t('access.pages')}</th>
                </tr>
              </thead>
              <tbody>
                {ultimas.map((lectura) => (
                  <tr key={lectura.sessionId}>
                    <td>
                      <span className="mono">{claveDeConsulta(lectura)}</span>
                      <br />
                      <span className="tenue" style={{ fontSize: '.78rem' }}>
                        {lectura.documentTitle}
                      </span>
                    </td>
                    <td className="mono">{fechaHora.format(new Date(lectura.startedAt))}</td>
                    <td className="mono">{duracionLegible(lectura.activeSeconds)}</td>
                    <td className="mono">{lectura.pagesViewed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="tarjeta__pie">{t('dashboard.readingNote')}</p>
      </section>
    </>
  );
}
